"use client";

import { c as _c } from "react/compiler-runtime";
import DropListItemContentMedia from "@/components/drops/view/item/content/media/DropListItemContentMedia";
import { MobileVotingModal, VotingModal } from "@/components/voting";
import VotingModalButton from "@/components/voting/VotingModalButton";
import DropMobileMenuHandler from "@/components/waves/drops/DropMobileMenuHandler";
import { getRankHoverBorderClass } from "@/components/waves/drops/dropRankStyles";
import WaveDropReactions from "@/components/waves/drops/WaveDropReactions";
import type { DropInteractionParams } from "@/components/waves/drops/drop.types";
import { DropLocation } from "@/components/waves/drops/drop.types";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useDropInteractionRules } from "@/hooks/drops/useDropInteractionRules";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { useCallback, useState } from "react";
import MemeDropActions from "./meme-participation-drop/MemeDropActions";
import MemeDropArtistInfo from "./meme-participation-drop/MemeDropArtistInfo";
import MemeDropDescription from "./meme-participation-drop/MemeDropDescription";
import MemeDropHeader from "./meme-participation-drop/MemeDropHeader";
import MemeDropVoteStats from "./meme-participation-drop/MemeDropVoteStats";
import MemeDropTraits from "./MemeDropTraits";
interface MemeParticipationDropProps {
  readonly drop: ExtendedDrop;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly onReply: (param: DropInteractionParams) => void;
}

// Border styling based on rank
const getBorderClasses = (drop: ExtendedDrop, isActiveDrop: boolean) => {
  const rank = typeof drop.rank === "number" && drop.rank <= 3 ? drop.rank : null;
  const baseClasses = "tw-rounded-xl tw-border tw-border-solid tw-border-iron-800 tw-transition-all tw-duration-200 tw-ease-out tw-overflow-hidden";
  if (isActiveDrop) {
    return `${baseClasses} desktop-hover:hover:tw-border-[#3CCB7F]/40 tw-bg-[#3CCB7F]/5`;
  }
  if (rank === 1) {
    return `${baseClasses} ${getRankHoverBorderClass(1)}`;
  }
  if (rank === 2) {
    return `${baseClasses} ${getRankHoverBorderClass(2)}`;
  }
  if (rank === 3) {
    return `${baseClasses} ${getRankHoverBorderClass(3)}`;
  }
  return `${baseClasses} tw-border-iron-800`;
};
export default function MemeParticipationDrop(t0) {
  const $ = _c(68);
  const {
    drop,
    activeDrop,
    showReplyAndQuote,
    location,
    onReply
  } = t0;
  const [isVotingModalOpen, setIsVotingModalOpen] = useState(false);
  const {
    canShowVote
  } = useDropInteractionRules(drop);
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const isMobile = useIsMobileDevice();
  const isMobileScreen = useIsMobileScreen();
  const title = drop.metadata.find(_temp)?.data_value ?? "Artwork Title";
  const description = drop.metadata.find(_temp2)?.data_value ?? "This is an artwork submission for The Memes collection.";
  let t1;
  if ($[0] !== drop.parts) {
    t1 = drop.parts.at(0)?.media.at(0);
    $[0] = drop.parts;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const artworkMedia = t1;
  let t2;
  if ($[2] !== drop || $[3] !== isActiveDrop) {
    t2 = getBorderClasses(drop, isActiveDrop);
    $[2] = drop;
    $[3] = isActiveDrop;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const borderClasses = t2;
  let t3;
  if ($[5] !== drop || $[6] !== onReply) {
    t3 = () => {
      onReply({
        drop,
        partId: drop.parts[0]?.part_id
      });
    };
    $[5] = drop;
    $[6] = onReply;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleOnReply = t3;
  const t4 = `tw-group tw-relative tw-w-full ${location === DropLocation.WAVE ? "tw-px-4 tw-py-1" : ""}`;
  const t5 = `${borderClasses} ${location === DropLocation.WAVE ? "tw-bg-iron-900/80" : "tw-bg-iron-950"}`;
  let t6;
  if ($[8] !== drop) {
    t6 = <MemeDropArtistInfo drop={drop} />;
    $[8] = drop;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== title) {
    t7 = <MemeDropHeader title={title} />;
    $[10] = title;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== description) {
    t8 = <MemeDropDescription description={description} />;
    $[12] = description;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t7 || $[15] !== t8) {
    t9 = <div className="tw-mt-2 tw-flex tw-flex-col sm:tw-ml-[3.25rem] sm:tw-mt-1.5">{t7}{t8}</div>;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t6 || $[18] !== t9) {
    t10 = <div className="tw-p-4">{t6}{t9}</div>;
    $[17] = t6;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== artworkMedia || $[21] !== location) {
    t11 = artworkMedia && <div className={`tw-flex tw-h-96 tw-justify-center ${location === DropLocation.WAVE ? "tw-bg-iron-800/30" : "tw-bg-iron-900/40"}`}><DropListItemContentMedia media_mime_type={artworkMedia.mime_type} media_url={artworkMedia.url} isCompetitionDrop={true} /></div>;
    $[20] = artworkMedia;
    $[21] = location;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== drop) {
    t12 = <div className="tw-px-4 tw-py-4"><MemeDropTraits drop={drop} /></div>;
    $[23] = drop;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t10 || $[26] !== t11 || $[27] !== t12) {
    t13 = <>{t10}{t11}{t12}</>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== drop || $[30] !== handleOnReply || $[31] !== showReplyAndQuote || $[32] !== t13) {
    t14 = <DropMobileMenuHandler drop={drop} showReplyAndQuote={showReplyAndQuote} onReply={handleOnReply}>{t13}</DropMobileMenuHandler>;
    $[29] = drop;
    $[30] = handleOnReply;
    $[31] = showReplyAndQuote;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== drop.context_profile_context || $[35] !== drop.raters_count || $[36] !== drop.rating || $[37] !== drop.rating_prediction || $[38] !== drop.top_raters || $[39] !== drop.wave.voting_credit_type) {
    t15 = <div className="tw-px-4 @[700px]:tw-px-0"><MemeDropVoteStats current={drop.rating} projected={drop.rating_prediction} votingCreditType={drop.wave.voting_credit_type} ratersCount={drop.raters_count} topVoters={drop.top_raters} userContext={drop.context_profile_context} /></div>;
    $[34] = drop.context_profile_context;
    $[35] = drop.raters_count;
    $[36] = drop.rating;
    $[37] = drop.rating_prediction;
    $[38] = drop.top_raters;
    $[39] = drop.wave.voting_credit_type;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== canShowVote || $[42] !== drop) {
    t16 = canShowVote && <div className="tw-flex tw-w-full tw-justify-center tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-iron-800 tw-px-6 tw-pt-4 @[700px]:tw-w-auto @[700px]:tw-border-none @[700px]:tw-px-0 @[700px]:tw-pt-0"><div onClick={_temp3}><VotingModalButton drop={drop} onClick={() => setIsVotingModalOpen(true)} /></div></div>;
    $[41] = canShowVote;
    $[42] = drop;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== t15 || $[45] !== t16) {
    t17 = <div className="tw-flex tw-flex-col tw-justify-between tw-gap-3 tw-pb-4 @[700px]:tw-flex-row @[700px]:tw-items-center @[700px]:tw-gap-y-0 @[700px]:tw-px-4">{t15}{t16}</div>;
    $[44] = t15;
    $[45] = t16;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== drop) {
    t18 = <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-px-4 tw-pb-4"><WaveDropReactions drop={drop} /></div>;
    $[47] = drop;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== drop || $[50] !== handleOnReply || $[51] !== isMobile || $[52] !== showReplyAndQuote) {
    t19 = <div className="tw-absolute tw-right-4 tw-top-2"><MemeDropActions drop={drop} isMobile={isMobile} showReplyAndQuote={showReplyAndQuote} onReply={handleOnReply} /></div>;
    $[49] = drop;
    $[50] = handleOnReply;
    $[51] = isMobile;
    $[52] = showReplyAndQuote;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] !== t14 || $[55] !== t17 || $[56] !== t18 || $[57] !== t19 || $[58] !== t5) {
    t20 = <div className={t5}>{t14}{t17}{t18}{t19}</div>;
    $[54] = t14;
    $[55] = t17;
    $[56] = t18;
    $[57] = t19;
    $[58] = t5;
    $[59] = t20;
  } else {
    t20 = $[59];
  }
  let t21;
  if ($[60] !== drop || $[61] !== isMobileScreen || $[62] !== isVotingModalOpen) {
    t21 = isMobileScreen ? <MobileVotingModal drop={drop} isOpen={isVotingModalOpen} onClose={() => setIsVotingModalOpen(false)} /> : <VotingModal drop={drop} isOpen={isVotingModalOpen} onClose={() => setIsVotingModalOpen(false)} />;
    $[60] = drop;
    $[61] = isMobileScreen;
    $[62] = isVotingModalOpen;
    $[63] = t21;
  } else {
    t21 = $[63];
  }
  let t22;
  if ($[64] !== t20 || $[65] !== t21 || $[66] !== t4) {
    t22 = <div className="tw-w-full tw-@container"><div className={t4}>{t20}{t21}</div></div>;
    $[64] = t20;
    $[65] = t21;
    $[66] = t4;
    $[67] = t22;
  } else {
    t22 = $[67];
  }
  return t22;
}
function _temp3(e) {
  return e.stopPropagation();
}
function _temp2(m_0) {
  return m_0.data_key === "description";
}
function _temp(m) {
  return m.data_key === "title";
}